/**
 * Hubert / Raleigh backyard presentation — separate SceneWorlds per layout.
 *
 * Desktop, tablet, and mobile share one logical place. Desktop and mobile each
 * have approved master artwork; tablet reuses the Mobile master with its own
 * prop transforms. The browser viewport crops into a fixed world via
 * WorldCamera; it never rescales the world pixel box.
 */

import {
  DEFAULT_RESPONSIVE_EXPLORE,
  type ResponsiveExploreConfig,
} from '@/world-prototype/camera/responsiveExploreZoom';
import type { QuadCorners } from '@/world-prototype/objects/quadShape';
import {
  physicalSizeForObjectId,
  resolvePhysicalWidthFrac,
} from '@/world-prototype/scale/worldScale';

export type { QuadCorners } from '@/world-prototype/objects/quadShape';
/**
 * Scene layout profile — drives prop/decal transforms and which SceneWorld
 * loads. Artwork plate is derived via {@link artworkPlateFor} (only two
 * masters exist: Desktop and Mobile).
 */
export type BackyardPresentationMode = 'desktop' | 'tablet' | 'mobile';

/**
 * Device-class from usable playfield size (not UA). Drives map fit and which
 * backyard layout a tablet should load; independent of editor Layout buttons.
 */
export type DeviceClass = 'desktop' | 'tablet' | 'mobile';

/**
 * Which master PNG to load. Tablet uses the Mobile master without a third
 * painted plate.
 */
export type BackyardArtworkPlate = 'desktop' | 'mobile';

/** Visual transform for one prop/decal/scatter in a layout profile. */
export interface PropLayoutTransform {
  x: number;
  y: number;
  /**
   * Absolute scenery width (world-width fraction).
   * When the object uses physical scale, this is the *computed* width at
   * resolve time; prefer `scale` (editor multiplier) when authoring.
   */
  widthFrac: number;
  /**
   * Editor / readability multiplier on recommended physical size.
   * `1` = recommended. Omitted = 1.
   */
  scale?: number;
  /**
   * Optional world-height fraction (independent of sprite aspect).
   * Soft ground decals (gravel) use this so width and length can be
   * dragged separately without freeform reshaping.
   */
  heightFrac?: number;
  rotationDeg?: number;
  depth?: number;
  /**
   * Mirror the sprite horizontally around its anchor (CSS scaleX(-1)).
   * Omitted / false = facing as authored in the PNG.
   */
  flipX?: boolean;
  /**
   * Optional freeform quad (TL→TR→BR→BL) as local offsets from center.
   * Ground decals use this so each side angle can be edited independently.
   */
  quadCorners?: QuadCorners;
}

/** Per-device visual transforms for one logical object. */
export interface LayoutProfilePair {
  desktop: PropLayoutTransform;
  tablet: PropLayoutTransform;
  mobile: PropLayoutTransform;
}

/** Approved Hubert desktop master (1672×941). */
export const BACKYARD_MASTER_DESKTOP_SRC = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/${encodeURIComponent('Master Desktop Backyard.png')}`;
export const BACKYARD_MASTER_DESKTOP_ASPECT = 1672 / 941;

/** Approved Hubert mobile master (1672×941). */
export const BACKYARD_MASTER_MOBILE_SRC = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/${encodeURIComponent('Master Mobile Backyard.png')}`;
export const BACKYARD_MASTER_MOBILE_ASPECT = 1672 / 941;

/** Approved Raleigh desktop master (1672×941) — from Raleigh Master 2.png. */
export const RALEIGH_MASTER_DESKTOP_SRC = `${import.meta.env.BASE_URL}art/environments/raleigh/backyard/${encodeURIComponent('Master Desktop Backyard.png')}`;
export const RALEIGH_MASTER_DESKTOP_ASPECT = 1672 / 941;

/** Approved Raleigh mobile master (1672×941) — from Raleigh Master 1.png. */
export const RALEIGH_MASTER_MOBILE_SRC = `${import.meta.env.BASE_URL}art/environments/raleigh/backyard/${encodeURIComponent('Master Mobile Backyard.png')}`;
export const RALEIGH_MASTER_MOBILE_ASPECT = 1672 / 941;

/**
 * Spanish Fork landscape master (1024×576, 16:9).
 * Desktop / tablet / mobile landscape profiles share this plate until a
 * dedicated portrait composition ships.
 */
export const SPANISH_FORK_MASTER_DESKTOP_SRC = `${import.meta.env.BASE_URL}art/environments/spanish-fork/backyard/${encodeURIComponent('Master Desktop Backyard.png')}`;
export const SPANISH_FORK_MASTER_DESKTOP_ASPECT = 1024 / 576;
/** Same landscape plate for tablet/mobile profiles (no portrait master yet). */
export const SPANISH_FORK_MASTER_MOBILE_SRC = `${import.meta.env.BASE_URL}art/environments/spanish-fork/backyard/${encodeURIComponent('Master Mobile Backyard.png')}`;
export const SPANISH_FORK_MASTER_MOBILE_ASPECT = 1024 / 576;

/**
 * Places map legacy desktop plate (1672×941) — retained for reference only.
 * Active Places UI selects landscape vs portrait masters by orientation.
 */
export const PLACES_MAP_MASTER_DESKTOP_SRC = `${import.meta.env.BASE_URL}art/environments/shared/map/${encodeURIComponent('Master Desktop Map.png')}`;
/**
 * Places map legacy mobile plate (1672×941) — retained for reference only.
 */
export const PLACES_MAP_MASTER_MOBILE_SRC = `${import.meta.env.BASE_URL}art/environments/shared/map/${encodeURIComponent('Master Mobile Map.png')}`;
/**
 * Places map landscape plate (1024×601) — desktop / tablet / phone landscape.
 * No baked-in pins; markers are independent UI over the plate.
 */
export const PLACES_MAP_MASTER_LANDSCAPE_SRC = `${import.meta.env.BASE_URL}art/environments/shared/map/${encodeURIComponent('Master Landscape Map.png')}`;
/**
 * Places map portrait plate (682×1024) — phone / tablet portrait.
 * No baked-in pins; markers are independent UI over the plate.
 */
export const PLACES_MAP_MASTER_PORTRAIT_SRC = `${import.meta.env.BASE_URL}art/environments/shared/map/${encodeURIComponent('Master Portrait Map.png')}`;
/** @deprecated Legacy landscape-ish plate aspect; prefer orientation sizes. */
export const PLACES_MAP_MASTER_ASPECT = 1672 / 941;
/** Landscape master aspect. */
export const PLACES_MAP_LANDSCAPE_ASPECT = 1024 / 601;
/** Portrait master aspect. */
export const PLACES_MAP_PORTRAIT_ASPECT = 682 / 1024;
/** Ocean fill behind contain letterboxing on the places map. */
export const PLACES_MAP_LETTERBOX_COLOR = '#64b0e2';

/**
 * Which places-map master to load — orientation only (two artwork profiles).
 */
export type PlacesMapOrientation = 'landscape' | 'portrait';

/**
 * Normalized rectangle (0–1 of the master plate) covering continental US + pins.
 * Kept for legacy helpers / tests; active Places fit always shows the full plate.
 */
export const PLACES_MAP_IMPORTANT_AREA = {
  left: 0.04,
  top: 0.06,
  right: 0.98,
  bottom: 0.94,
} as const;

/** @deprecated Legacy plate size; active UI uses landscape/portrait sizes. */
export const PLACES_MAP_MASTER_SIZE = { width: 1672, height: 941 } as const;
/** Landscape master plate pixel size. */
export const PLACES_MAP_LANDSCAPE_SIZE = { width: 1024, height: 601 } as const;
/** Portrait master plate pixel size. */
export const PLACES_MAP_PORTRAIT_SIZE = { width: 682, height: 1024 } as const;

/** Resolve places-map plate orientation from playfield aspect. */
export function placesMapOrientationFor(
  playfieldWidth: number,
  playfieldHeight: number,
): PlacesMapOrientation {
  return playfieldHeight >= playfieldWidth ? 'portrait' : 'landscape';
}

/** Pixel size of the active places-map master for contain framing. */
export function placesMapMasterSizeFor(
  orientation: PlacesMapOrientation,
): { width: number; height: number } {
  return orientation === 'landscape'
    ? PLACES_MAP_LANDSCAPE_SIZE
    : PLACES_MAP_PORTRAIT_SIZE;
}

/**
 * Kid-facing Places title (HUD + aria). Kept out of a heavy page header so the
 * map stays the hero.
 */
export const PLACES_MAP_TITLE = 'Where should we go?';

/**
 * Places-map fit policy.
 * Always contain-style — never cover-crop destinations off-screen.
 * `contain` shows the full master plate (preferred).
 * `important-contain` is retained for legacy callers/tests only.
 */
export type PlacesMapFit = 'cover' | 'contain' | 'important-contain';

/** Focus point for legacy cover framing (center of {@link PLACES_MAP_IMPORTANT_AREA}). */
export function placesMapImportantFocus(): { x: number; y: number } {
  return {
    x: (PLACES_MAP_IMPORTANT_AREA.left + PLACES_MAP_IMPORTANT_AREA.right) / 2,
    y: (PLACES_MAP_IMPORTANT_AREA.top + PLACES_MAP_IMPORTANT_AREA.bottom) / 2,
  };
}

/**
 * Max fraction of the playfield the map plate may occupy (contain inset).
 * Portrait phones shrink to ~82% so ocean frames the US; desktop uses more.
 */
export function placesMapContainMaxFraction(
  playfieldWidth: number,
  playfieldHeight: number,
  deviceClass: DeviceClass,
): number {
  const portrait = playfieldHeight >= playfieldWidth;
  if (deviceClass === 'mobile') return portrait ? 0.82 : 0.94;
  if (deviceClass === 'tablet') return portrait ? 0.88 : 0.94;
  return 0.96;
}

/**
 * Places-map object-fit policy — always full-plate contain (no cover crop).
 */
export function resolvePlacesMapFit(
  _playfieldWidth: number,
  _playfieldHeight: number,
  _deviceClass: DeviceClass,
): PlacesMapFit {
  return 'contain';
}

/** CSS-pixel framing for map plate contain into a viewport. */
export interface PlacesMapImportantContainFrame {
  /** Uniform scale applied to the master plate. */
  scale: number;
  /** Displayed image width (px). */
  displayWidth: number;
  /** Displayed image height (px). */
  displayHeight: number;
  /** Image left edge relative to the playfield (px; may be negative). */
  offsetX: number;
  /** Image top edge relative to the playfield (px; may be negative). */
  offsetY: number;
}

/** Alias kept for call sites that talk about contain framing. */
export type PlacesMapContainFrame = PlacesMapImportantContainFrame;

/**
 * Contain the full master plate in the viewport (optionally inset), centered.
 * Unused viewport uses ocean fill — never cream/gray letterboxing.
 */
export function placesMapContainFrame(
  playfieldWidth: number,
  playfieldHeight: number,
  maxFraction = 1,
  imageWidth: number = PLACES_MAP_MASTER_SIZE.width,
  imageHeight: number = PLACES_MAP_MASTER_SIZE.height,
): PlacesMapContainFrame {
  const vw = Math.max(1, playfieldWidth);
  const vh = Math.max(1, playfieldHeight);
  const frac = Math.min(1, Math.max(0.5, maxFraction));
  const usableW = vw * frac;
  const usableH = vh * frac;
  const imgW = Math.max(1, imageWidth);
  const imgH = Math.max(1, imageHeight);
  const scale = Math.min(usableW / imgW, usableH / imgH);
  const displayWidth = imgW * scale;
  const displayHeight = imgH * scale;
  return {
    scale,
    displayWidth,
    displayHeight,
    offsetX: (vw - displayWidth) / 2,
    offsetY: (vh - displayHeight) / 2,
  };
}

/**
 * Contain {@link PLACES_MAP_IMPORTANT_AREA} in the viewport, centered.
 * Outer artwork may overflow (crop); unused viewport uses ocean fill.
 * Legacy landscape helper — active Places UI uses {@link placesMapContainFrame}.
 */
export function placesMapImportantContainFrame(
  playfieldWidth: number,
  playfieldHeight: number,
  imageWidth: number = PLACES_MAP_MASTER_SIZE.width,
  imageHeight: number = PLACES_MAP_MASTER_SIZE.height,
  area: typeof PLACES_MAP_IMPORTANT_AREA = PLACES_MAP_IMPORTANT_AREA,
): PlacesMapImportantContainFrame {
  const vw = Math.max(1, playfieldWidth);
  const vh = Math.max(1, playfieldHeight);
  const imgW = Math.max(1, imageWidth);
  const imgH = Math.max(1, imageHeight);
  const impW = Math.max(1e-4, (area.right - area.left) * imgW);
  const impH = Math.max(1e-4, (area.bottom - area.top) * imgH);
  const scale = Math.min(vw / impW, vh / impH);
  const displayWidth = imgW * scale;
  const displayHeight = imgH * scale;
  const focusX = (area.left + area.right) / 2;
  const focusY = (area.top + area.bottom) / 2;
  return {
    scale,
    displayWidth,
    displayHeight,
    offsetX: vw / 2 - focusX * displayWidth,
    offsetY: vh / 2 - focusY * displayHeight,
  };
}

/**
 * Map a point in image space (0–1) into viewport space under full-plate contain.
 */
export function containFrameImagePointToViewport(
  imageX: number,
  imageY: number,
  frame: PlacesMapContainFrame,
  playfieldWidth: number,
  playfieldHeight: number,
): { x: number; y: number } {
  return {
    x: (frame.offsetX + imageX * frame.displayWidth) / Math.max(1, playfieldWidth),
    y: (frame.offsetY + imageY * frame.displayHeight) / Math.max(1, playfieldHeight),
  };
}

/**
 * True when a cover crop of the master plate into the viewport can keep the
 * entire important area visible (no dedicated landscape map asset needed).
 */
export function placesMapImportantAreaFitsCover(
  playfieldWidth: number,
  playfieldHeight: number,
  imageAspect: number = PLACES_MAP_MASTER_ASPECT,
): boolean {
  const view = playfieldWidth / Math.max(1, playfieldHeight);
  const img = Math.max(0.01, imageAspect);
  const area = PLACES_MAP_IMPORTANT_AREA;
  const focus = placesMapImportantFocus();
  let left: number;
  let right: number;
  let top: number;
  let bottom: number;
  if (img > view) {
    const visible = view / img;
    const maxLeft = 1 - visible;
    left = Math.min(maxLeft, Math.max(0, focus.x - visible / 2));
    right = left + visible;
    top = 0;
    bottom = 1;
  } else {
    const visible = img / view;
    const maxTop = 1 - visible;
    top = Math.min(maxTop, Math.max(0, focus.y - visible / 2));
    bottom = top + visible;
    left = 0;
    right = 1;
  }
  return (
    area.left >= left - 1e-6 &&
    area.right <= right + 1e-6 &&
    area.top >= top - 1e-6 &&
    area.bottom <= bottom + 1e-6
  );
}

/**
 * Map a point in image space (0–1) into viewport space under important-contain.
 * Returns null when the point falls outside the viewport.
 */
export function importantContainImagePointToViewport(
  imageX: number,
  imageY: number,
  playfieldWidth: number,
  playfieldHeight: number,
): { x: number; y: number } | null {
  const frame = placesMapImportantContainFrame(playfieldWidth, playfieldHeight);
  const x = (frame.offsetX + imageX * frame.displayWidth) / playfieldWidth;
  const y = (frame.offsetY + imageY * frame.displayHeight) / playfieldHeight;
  if (x < -0.02 || x > 1.02 || y < -0.02 || y > 1.02) return null;
  return { x, y };
}

/** Map an image-space size into viewport fractions under important-contain. */
export function importantContainImageSizeToViewport(
  imageWidth: number,
  imageHeight: number,
  playfieldWidth: number,
  playfieldHeight: number,
): { width: number; height: number } {
  const frame = placesMapImportantContainFrame(playfieldWidth, playfieldHeight);
  return {
    width: (imageWidth * frame.displayWidth) / Math.max(1, playfieldWidth),
    height: (imageHeight * frame.displayHeight) / Math.max(1, playfieldHeight),
  };
}

/**
 * Normalized rectangle (0–1 of the master plate) covering the fenced yard.
 * Desktop: `resolvePlayAreaExploreZoom` contain/cover-bump.
 * Mobile: framing center for fence-centered cover overscan only.
 */
export interface ArtworkPlayArea {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Fenced play areas for Hubert masters (walk polygon AABB + back fence).
 */
export const BACKYARD_PLAY_AREA: Readonly<
  Record<BackyardPresentationMode, ArtworkPlayArea>
> = {
  // Back fence ~0.30–0.33; sides nearly full width at the patio.
  desktop: { left: 0.02, top: 0.3, right: 0.98, bottom: 1 },
  /**
   * Mobile / tablet masters share fence framing. Tablet uses the Mobile PNG
   * with independent prop transforms — play-area AABB matches mobile.
   */
  tablet: { left: 0.06, top: 0.28, right: 0.94, bottom: 1 },
  /**
   * Mobile master fence — used only to center the cover crop on the yard,
   * not for contain/cover playArea math.
   */
  mobile: { left: 0.06, top: 0.28, right: 0.94, bottom: 1 },
};

/** Raleigh masters share the same fence framing convention. */
export const RALEIGH_PLAY_AREA = BACKYARD_PLAY_AREA;

/**
 * Spanish Fork — open grass lawn framed by gravel (left) and dirt berms
 * (back / right). No fence; frame the walkable grass bowl.
 */
export const SPANISH_FORK_PLAY_AREA: Readonly<
  Record<BackyardPresentationMode, ArtworkPlayArea>
> = {
  desktop: { left: 0.18, top: 0.26, right: 0.92, bottom: 1 },
  tablet: { left: 0.18, top: 0.26, right: 0.92, bottom: 1 },
  mobile: { left: 0.18, top: 0.26, right: 0.92, bottom: 1 },
};

export function backyardPlayAreaFor(
  mode: BackyardPresentationMode,
): ArtworkPlayArea {
  return BACKYARD_PLAY_AREA[mode];
}

/**
 * Editor viewport size hints for optional runtime-preview tooling only.
 * The World Editor canvas itself is always full-bleed — do not use these
 * to shrink the editing playfield.
 */
export const BACKYARD_EDITOR_VIEWPORT = {
  desktop: { width: 1600, height: 900 },
  tablet: { width: 768, height: 1024 },
  mobile: { width: 390, height: 844 },
} as const;

/**
 * True when the playfield should load the Mobile Backyard plate.
 *
 * Narrow phones: width < tabletBreakpoint (768).
 * Landscape phones: height < landscapePhoneMaxHeight (500) AND
 *   width < landscapePhoneMaxWidth (1000).
 *
 * A short desktop window (e.g. 1600×480) is NOT mobile — only the camera crops.
 */
export function isMobilePresentationPlayfield(
  playfieldWidth: number,
  playfieldHeight: number,
  config: Partial<ResponsiveExploreConfig> = {},
): boolean {
  const merged = { ...DEFAULT_RESPONSIVE_EXPLORE, ...config };
  const w = playfieldWidth;
  const h = playfieldHeight;
  const isNarrowPhone = w < merged.tabletBreakpoint;
  const isLandscapePhone =
    h < merged.landscapePhoneMaxHeight && w < merged.landscapePhoneMaxWidth;
  return isNarrowPhone || isLandscapePhone;
}

/**
 * Resolve DESKTOP / TABLET / MOBILE from usable playfield size.
 *
 * Phone rules are unchanged. Wide short desktop windows stay Desktop (plate +
 * cover crop). Mid-size iPads become Tablet so scenes can choose artwork.
 * Large 4:3 tablets (e.g. iPad Pro landscape) stay Tablet so the map can
 * important-contain, while backyard may still load the Desktop plate.
 */
export function resolveDeviceClass(
  playfieldWidth: number,
  playfieldHeight: number,
  config: Partial<ResponsiveExploreConfig> = {},
): DeviceClass {
  if (isMobilePresentationPlayfield(playfieldWidth, playfieldHeight, config)) {
    return 'mobile';
  }
  const merged = { ...DEFAULT_RESPONSIVE_EXPLORE, ...config };
  const w = playfieldWidth;
  const h = playfieldHeight;
  if (w >= merged.desktopFitBreakpoint && h >= merged.desktopFitMinHeight) {
    const aspect = w / Math.max(1, h);
    // ~4:3 large tablets — treat as tablet for map fit; backyard layout may
    // still pick the Desktop plate when landscape room is sufficient.
    if (
      aspect < merged.largeTabletMaxAspect &&
      w < merged.largeTabletMaxWidth
    ) {
      return 'tablet';
    }
    return 'desktop';
  }
  // Short wide desktop window (e.g. 1600×480) — Desktop plate + camera crop.
  if (w >= merged.desktopFitBreakpoint) return 'desktop';
  return 'tablet';
}

/**
 * Choose Desktop / Tablet / Mobile *layout* for the backyard SceneWorld.
 *
 * Device class and layout are related but not identical:
 * - Mobile device → mobile layout (LOCKED).
 * - Desktop device → desktop layout (LOCKED).
 * - Tablet portrait / compact landscape → tablet layout (Mobile art + tablet props).
 * - Tablet landscape with enough room → desktop layout (large iPad).
 */
export function resolveBackyardLayoutMode(
  playfieldWidth: number,
  playfieldHeight: number,
  config: Partial<ResponsiveExploreConfig> = {},
  deviceClass: DeviceClass = resolveDeviceClass(
    playfieldWidth,
    playfieldHeight,
    config,
  ),
): BackyardPresentationMode {
  if (deviceClass === 'mobile') return 'mobile';
  if (deviceClass === 'desktop') return 'desktop';

  const merged = { ...DEFAULT_RESPONSIVE_EXPLORE, ...config };
  const w = playfieldWidth;
  const h = playfieldHeight;
  // Large tablet landscape: keep the approved Desktop Backyard + desktop props.
  if (
    h < w &&
    w >= merged.tabletDesktopMinWidth &&
    h >= merged.tabletDesktopMinHeight
  ) {
    return 'desktop';
  }
  // Compact tablet (portrait or smaller landscape): tablet prop profile.
  return 'tablet';
}

/**
 * Resolve backyard SceneWorld layout from usable playfield size.
 *
 * Prefer {@link resolveDeviceClass} + {@link resolveBackyardLayoutMode} when
 * both device class and layout are needed (badge, map fit).
 */
export function getBackyardPresentationMode(
  playfieldWidth: number,
  playfieldHeight: number,
  config: Partial<ResponsiveExploreConfig> = {},
): BackyardPresentationMode {
  return resolveBackyardLayoutMode(playfieldWidth, playfieldHeight, config);
}

/** Map a layout profile to the master PNG plate (tablet → Mobile art). */
export function artworkPlateFor(
  mode: BackyardPresentationMode,
): BackyardArtworkPlate {
  return mode === 'desktop' ? 'desktop' : 'mobile';
}

/** True when the layout uses Mobile-master cover camera (mobile + tablet). */
export function usesMobileCoverCamera(
  mode: BackyardPresentationMode,
): boolean {
  return mode === 'mobile' || mode === 'tablet';
}

export function backyardMasterFor(mode: BackyardPresentationMode): {
  src: string;
  alt: string;
  aspectRatio: number;
} {
  if (artworkPlateFor(mode) === 'mobile') {
    return {
      src: BACKYARD_MASTER_MOBILE_SRC,
      alt:
        mode === 'tablet'
          ? 'Hubert backyard — tablet (mobile master)'
          : 'Hubert backyard — mobile master',
      aspectRatio: BACKYARD_MASTER_MOBILE_ASPECT,
    };
  }
  return {
    src: BACKYARD_MASTER_DESKTOP_SRC,
    alt: 'Hubert backyard — desktop master',
    aspectRatio: BACKYARD_MASTER_DESKTOP_ASPECT,
  };
}

export function raleighMasterFor(mode: BackyardPresentationMode): {
  src: string;
  alt: string;
  aspectRatio: number;
} {
  if (artworkPlateFor(mode) === 'mobile') {
    return {
      src: RALEIGH_MASTER_MOBILE_SRC,
      alt:
        mode === 'tablet'
          ? 'Holly Springs backyard — tablet (mobile master)'
          : 'Holly Springs backyard — mobile master',
      aspectRatio: RALEIGH_MASTER_MOBILE_ASPECT,
    };
  }
  return {
    src: RALEIGH_MASTER_DESKTOP_SRC,
    alt: 'Holly Springs backyard — desktop master',
    aspectRatio: RALEIGH_MASTER_DESKTOP_ASPECT,
  };
}

export function backyardAspectFor(mode: BackyardPresentationMode): number {
  return backyardMasterFor(mode).aspectRatio;
}

export function raleighAspectFor(mode: BackyardPresentationMode): number {
  return raleighMasterFor(mode).aspectRatio;
}

export function spanishForkMasterFor(mode: BackyardPresentationMode): {
  src: string;
  alt: string;
  aspectRatio: number;
} {
  // Landscape plate for every layout profile until a portrait master exists.
  if (artworkPlateFor(mode) === 'mobile') {
    return {
      src: SPANISH_FORK_MASTER_MOBILE_SRC,
      alt:
        mode === 'tablet'
          ? 'Spanish Fork backyard — tablet (landscape master)'
          : 'Spanish Fork backyard — mobile (landscape master)',
      aspectRatio: SPANISH_FORK_MASTER_MOBILE_ASPECT,
    };
  }
  return {
    src: SPANISH_FORK_MASTER_DESKTOP_SRC,
    alt: 'Spanish Fork backyard — desktop master',
    aspectRatio: SPANISH_FORK_MASTER_DESKTOP_ASPECT,
  };
}

export function spanishForkAspectFor(mode: BackyardPresentationMode): number {
  return spanishForkMasterFor(mode).aspectRatio;
}

/**
 * Resolve places-map master artwork by orientation only.
 * Portrait → Master Portrait Map; landscape → Master Landscape Map.
 * (`mode` retained for call-site compatibility; unused for plate selection.)
 */
export function placesMapMasterFor(
  _mode: BackyardPresentationMode,
  orientation: PlacesMapOrientation = 'portrait',
): {
  src: string;
  alt: string;
  aspectRatio: number;
} {
  if (orientation === 'landscape') {
    return {
      src: PLACES_MAP_MASTER_LANDSCAPE_SRC,
      alt: 'PorchLight places map — landscape',
      aspectRatio: PLACES_MAP_LANDSCAPE_ASPECT,
    };
  }
  return {
    src: PLACES_MAP_MASTER_PORTRAIT_SRC,
    alt: 'PorchLight places map — portrait',
    aspectRatio: PLACES_MAP_PORTRAIT_ASPECT,
  };
}

/**
 * Destination marker on the places map (coords in master-plate 0–1 space).
 * Visual + hit size are resolved independently of map scale at layout time.
 */
export interface PlacesMapPinHit {
  /** Matches `utilityTravel[].id` on the places-map hub. */
  utilityId: string;
  /** Accessible name for aria / title. */
  label: string;
  /** Kid-facing destination label (Spanish Fork / Holly Springs / Hubert). */
  shortLabel: string;
  /** Pin-head center X in image coords (normalized to rendered map bounds). */
  x: number;
  /** Pin-head center Y in image coords. */
  y: number;
  /** Marker accent matching the painted map pins. */
  color: string;
  /**
   * Where the parchment bubble sits around the marker. The marker never
   * moves off its geography; only the bubble swings away so crowded
   * neighbours (Holly Springs / Hubert) each get their own tap target.
   */
  labelPlacement: PlacesMapLabelPlacement;
}

/** Bubble direction relative to the marker it belongs to. */
export type PlacesMapLabelPlacement =
  | 'below'
  | 'above'
  | 'left'
  | 'right'
  | 'above-left'
  | 'above-right'
  | 'below-left'
  | 'below-right';

/** Independent pin chrome sizes (CSS px) — not baked into map scale. */
export interface PlacesMapPinMetrics {
  /** Visible teardrop marker size (CSS px). */
  markerSize: number;
  /** Minimum interactive hit edge (CSS px). */
  hitMin: number;
  /** Destination label font size (CSS px). */
  labelFontSize: number;
  /** Approx. visual scale vs. prior phone painted pin (~28–32px). */
  visualScale: number;
}

/**
 * Pin metrics by device. Portrait phones get the largest markers / hits
 * (~64px+); landscape phones stay kid-sized; desktop/tablet remain easy taps.
 */
export function resolvePlacesMapPinMetrics(
  deviceClass: DeviceClass,
  playfieldWidth: number,
  playfieldHeight: number,
): PlacesMapPinMetrics {
  const portrait = playfieldHeight >= playfieldWidth;
  if (deviceClass === 'mobile') {
    return portrait
      ? {
          markerSize: 52,
          hitMin: 72,
          labelFontSize: 14,
          visualScale: 1.7,
        }
      : {
          markerSize: 42,
          hitMin: 64,
          labelFontSize: 13,
          visualScale: 1.45,
        };
  }
  if (deviceClass === 'tablet') {
    return portrait
      ? {
          markerSize: 46,
          hitMin: 68,
          labelFontSize: 14,
          visualScale: 1.4,
        }
      : {
          markerSize: 44,
          hitMin: 64,
          labelFontSize: 13,
          visualScale: 1.3,
        };
  }
  return {
    markerSize: 40,
    hitMin: 56,
    labelFontSize: 13,
    visualScale: 1.15,
  };
}

/** Axis-aligned hit rect in viewport CSS px. */
export interface PlacesMapPinHitRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Vertical gap between marker and label in the flow column.
 * Must match `--pin-label-gap` in the places-map pin styles.
 */
export const PLACES_MAP_PIN_LABEL_GAP = 3;

/** Breathing room between the marker edge and a swung-out bubble. */
const PLACES_MAP_LABEL_CLEARANCE_X = 6;
/** Slack added around the marker + bubble union so small fingers land. */
const PLACES_MAP_HIT_PAD = 4;
/** Keep bubbles off the very edge of the playfield. */
const PLACES_MAP_EDGE_MARGIN = 4;
/** Never shrink a tap target below this, even when resolving overlap. */
const PLACES_MAP_HIT_FLOOR = 28;
/** How much of a full side-swing a diagonal placement uses. */
const PLACES_MAP_DIAGONAL_X_FRACTION = 0.5;

/** Measured parchment-bubble box (CSS px, untransformed). */
export interface PlacesMapLabelSize {
  width: number;
  height: number;
}

export interface PlacesMapPinBoxInput {
  /** Geographic anchor for the pin, in playfield px. */
  anchorXPx: number;
  anchorYPx: number;
  metrics: PlacesMapPinMetrics;
  placement: PlacesMapLabelPlacement;
  label: PlacesMapLabelSize;
  /** Measured size of the marker + label column (CSS px). */
  visualHeight: number;
  visualWidth: number;
  playfieldWidth: number;
  playfieldHeight: number;
}

export interface PlacesMapPinBox {
  /** Button hit rect covering marker plus its bubble. */
  hit: PlacesMapPinHitRect;
  /** Bubble translation away from its flow slot beneath the marker (px). */
  labelDx: number;
  labelDy: number;
  /** Where the marker actually renders — unchanged by placement. */
  markerXPx: number;
  markerYPx: number;
  /** Leader line tying the bubble back to the pin tip. */
  leader: PlacesMapPinLeader;
}

/**
 * Pointer from the pin tip to the bubble, in coordinates local to the
 * marker + label column (origin at the column's top-left).
 */
export interface PlacesMapPinLeader {
  /** Pin tip — the spot the callout names. */
  tipXPx: number;
  tipYPx: number;
  /** Length and angle to the bubble centre. */
  lengthPx: number;
  angleRad: number;
  /** False when the bubble already touches its marker (no pointer needed). */
  visible: boolean;
}

/** Below this the bubble sits on its marker and a pointer just adds noise. */
const PLACES_MAP_LEADER_MIN = 10;

/**
 * Pointer geometry for one pin, in column-local px.
 *
 * The tip is the bottom of the teardrop — the exact spot on the map — so the
 * callout keeps naming that place no matter which way the bubble swings.
 */
export function placesMapPinLeader(input: {
  /** Measured width of the marker + label column. */
  visualWidth: number;
  markerSize: number;
  label: PlacesMapLabelSize;
  labelDx: number;
  labelDy: number;
}): PlacesMapPinLeader {
  const { visualWidth, markerSize, label, labelDx, labelDy } = input;
  const tipXPx = visualWidth / 2;
  const tipYPx = markerSize;
  const bubbleX = visualWidth / 2 + labelDx;
  const bubbleY =
    markerSize + PLACES_MAP_PIN_LABEL_GAP + label.height / 2 + labelDy;
  const dx = bubbleX - tipXPx;
  const dy = bubbleY - tipYPx;
  const lengthPx = Math.hypot(dx, dy);
  return {
    tipXPx,
    tipYPx,
    lengthPx,
    angleRad: Math.atan2(dy, dx),
    visible: lengthPx > PLACES_MAP_LEADER_MIN + label.height / 2,
  };
}

/**
 * Resolve one pin's tap target and bubble offset.
 *
 * The column is centred on the anchor and lifted 55% of its height by CSS,
 * so the marker keeps its painted-map position regardless of where the
 * bubble swings; only a transform moves the bubble, never layout.
 */
export function placesMapPinBox(input: PlacesMapPinBoxInput): PlacesMapPinBox {
  const {
    anchorXPx,
    anchorYPx,
    metrics,
    placement,
    label,
    visualHeight,
    visualWidth,
    playfieldWidth,
    playfieldHeight,
  } = input;

  const marker = metrics.markerSize;
  const markerXPx = anchorXPx;
  const markerYPx = anchorYPx - visualHeight * 0.55 + marker / 2;

  const dirX = placement.includes('left')
    ? -1
    : placement.includes('right')
      ? 1
      : 0;
  const dirY = placement.includes('above')
    ? -1
    : placement.includes('below')
      ? 1
      : 0;

  const halfW = label.width / 2;
  const halfH = label.height / 2;
  // A diagonal only needs a partial sideways nudge: the vertical step already
  // clears the marker, so a full side-swing would fling a wide bubble far
  // across the map and read as labelling somewhere else entirely.
  const sideways = dirY === 0 ? 1 : PLACES_MAP_DIAGONAL_X_FRACTION;
  const wantX =
    markerXPx +
    dirX * (marker / 2 + halfW + PLACES_MAP_LABEL_CLEARANCE_X) * sideways;
  const wantY =
    markerYPx + dirY * (marker / 2 + halfH + PLACES_MAP_PIN_LABEL_GAP);

  const labelXPx = clamp(
    wantX,
    PLACES_MAP_EDGE_MARGIN + halfW,
    Math.max(
      PLACES_MAP_EDGE_MARGIN + halfW,
      playfieldWidth - PLACES_MAP_EDGE_MARGIN - halfW,
    ),
  );
  const labelYPx = clamp(
    wantY,
    PLACES_MAP_EDGE_MARGIN + halfH,
    Math.max(
      PLACES_MAP_EDGE_MARGIN + halfH,
      playfieldHeight - PLACES_MAP_EDGE_MARGIN - halfH,
    ),
  );

  // Flow slot: centred directly under the marker (`below` = zero transform).
  const flowYPx = markerYPx + marker / 2 + PLACES_MAP_PIN_LABEL_GAP + halfH;

  let left = Math.min(markerXPx - marker / 2, labelXPx - halfW);
  let right = Math.max(markerXPx + marker / 2, labelXPx + halfW);
  let top = Math.min(markerYPx - marker / 2, labelYPx - halfH);
  let bottom = Math.max(markerYPx + marker / 2, labelYPx + halfH);
  left -= PLACES_MAP_HIT_PAD;
  right += PLACES_MAP_HIT_PAD;
  top -= PLACES_MAP_HIT_PAD;
  bottom += PLACES_MAP_HIT_PAD;

  // Kid-sized floor, grown around the marker rather than the bubble.
  const growX = metrics.hitMin - (right - left);
  if (growX > 0) {
    left -= growX / 2;
    right += growX / 2;
  }
  const growY = metrics.hitMin - (bottom - top);
  if (growY > 0) {
    top -= growY / 2;
    bottom += growY / 2;
  }

  const width = Math.min(right - left, playfieldWidth);
  const height = Math.min(bottom - top, playfieldHeight);
  const hit: PlacesMapPinHitRect = {
    left: clamp(left, 0, Math.max(0, playfieldWidth - width)),
    top: clamp(top, 0, Math.max(0, playfieldHeight - height)),
    width,
    height,
  };

  const labelDx = labelXPx - markerXPx;
  const labelDy = labelYPx - flowYPx;
  return {
    hit,
    labelDx,
    labelDy,
    markerXPx,
    markerYPx,
    leader: placesMapPinLeader({
      visualWidth,
      markerSize: marker,
      label,
      labelDx,
      labelDy,
    }),
  };
}

/** One pin's rect plus the marker that must stay inside it. */
export interface PlacesMapPinSeparationInput {
  hit: PlacesMapPinHitRect;
  markerXPx: number;
  markerYPx: number;
}

/**
 * Trim any residual overlap so a tap can only ever hit one destination.
 * The split runs along whichever axis the pair overlaps least, so bubbles
 * stacked above/below each other keep their full width.
 */
export function separatePlacesMapPinBoxes(
  boxes: readonly PlacesMapPinSeparationInput[],
): PlacesMapPinHitRect[] {
  const rects = boxes.map((box) => ({ ...box.hit }));
  for (let i = 0; i < rects.length; i += 1) {
    for (let j = i + 1; j < rects.length; j += 1) {
      const a = rects[i]!;
      const b = rects[j]!;
      const overlapX = axisOverlap(a.left, a.width, b.left, b.width);
      const overlapY = axisOverlap(a.top, a.height, b.top, b.height);
      if (overlapX <= 0 || overlapY <= 0) continue;

      const ax = boxes[i]!.markerXPx;
      const bx = boxes[j]!.markerXPx;
      const ay = boxes[i]!.markerYPx;
      const by = boxes[j]!.markerYPx;
      const canSplitX = Math.abs(ax - bx) >= 1;
      const canSplitY = Math.abs(ay - by) >= 1;
      const splitVertically = canSplitY && (!canSplitX || overlapY <= overlapX);

      if (splitVertically) {
        const split = (ay + by) / 2;
        const [near, far] = ay < by ? [a, b] : [b, a];
        trimEnd(near, split, 'top', 'height');
        trimStart(far, split, 'top', 'height');
      } else if (canSplitX) {
        const split = (ax + bx) / 2;
        const [near, far] = ax < bx ? [a, b] : [b, a];
        trimEnd(near, split, 'left', 'width');
        trimStart(far, split, 'left', 'width');
      }
    }
  }
  return rects;
}

function axisOverlap(
  aStart: number,
  aSize: number,
  bStart: number,
  bSize: number,
): number {
  return Math.min(aStart + aSize, bStart + bSize) - Math.max(aStart, bStart);
}

function trimEnd(
  rect: PlacesMapPinHitRect,
  edge: number,
  startKey: 'left' | 'top',
  sizeKey: 'width' | 'height',
): void {
  const size = Math.max(PLACES_MAP_HIT_FLOOR, edge - rect[startKey]);
  rect[sizeKey] = Math.min(rect[sizeKey], size);
}

function trimStart(
  rect: PlacesMapPinHitRect,
  edge: number,
  startKey: 'left' | 'top',
  sizeKey: 'width' | 'height',
): void {
  const end = rect[startKey] + rect[sizeKey];
  const start = Math.min(edge, end - PLACES_MAP_HIT_FLOOR);
  if (start <= rect[startKey]) return;
  rect[startKey] = start;
  rect[sizeKey] = end - start;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Portrait pin coords — calibrated to Master Portrait Map (682×1024). */
const PLACES_MAP_PIN_DEFS_PORTRAIT: readonly PlacesMapPinHit[] = [
  {
    utilityId: 'to-spanish-fork',
    label: 'Spanish Fork, Utah',
    shortLabel: 'Spanish Fork',
    x: 0.25,
    y: 0.42,
    color: '#3f9b4a',
    labelPlacement: 'below',
  },
  {
    utilityId: 'to-raleigh',
    label: 'Holly Springs, North Carolina',
    shortLabel: 'Holly Springs',
    x: 0.79,
    y: 0.54,
    color: '#2f5f9a',
    // Bubble swings inland; its pointer stays on the pin so the callout
    // still names this exact spot and not the state it floats over.
    labelPlacement: 'above-left',
  },
  {
    utilityId: 'to-hubert',
    label: 'Hubert, North Carolina',
    shortLabel: 'Hubert',
    // East of Holly Springs on the NC coast; spaced for kid hit separation.
    x: 0.88,
    y: 0.57,
    color: '#5eb0d8',
    // Opposite direction from Holly Springs, pointer back to the coast.
    labelPlacement: 'below-right',
  },
];

/**
 * Landscape pin coords — calibrated to Master Landscape Map (1024×601).
 * Pins sit on geography; labels bias apart for Holly Springs / Hubert.
 */
const PLACES_MAP_PIN_DEFS_LANDSCAPE: readonly PlacesMapPinHit[] = [
  {
    utilityId: 'to-spanish-fork',
    label: 'Spanish Fork, Utah',
    shortLabel: 'Spanish Fork',
    x: 0.245,
    y: 0.45,
    color: '#3f9b4a',
    labelPlacement: 'below',
  },
  {
    utilityId: 'to-raleigh',
    label: 'Holly Springs, North Carolina',
    shortLabel: 'Holly Springs',
    x: 0.78,
    y: 0.545,
    color: '#2f5f9a',
    labelPlacement: 'above-left',
  },
  {
    utilityId: 'to-hubert',
    label: 'Hubert, North Carolina',
    shortLabel: 'Hubert',
    x: 0.83,
    y: 0.58,
    color: '#5eb0d8',
    labelPlacement: 'below-right',
  },
];

/**
 * Destination pins by orientation profile.
 * Green → Spanish Fork, dark blue → Holly Springs, light blue → Hubert.
 */
export const PLACES_MAP_PINS_BY_ORIENTATION: Readonly<
  Record<PlacesMapOrientation, readonly PlacesMapPinHit[]>
> = {
  portrait: PLACES_MAP_PIN_DEFS_PORTRAIT,
  landscape: PLACES_MAP_PIN_DEFS_LANDSCAPE,
};

/**
 * @deprecated Prefer {@link PLACES_MAP_PINS_BY_ORIENTATION}. Kept so existing
 * plate-keyed tests still resolve portrait coords.
 */
export const PLACES_MAP_PINS: Readonly<
  Record<BackyardArtworkPlate, readonly PlacesMapPinHit[]>
> = {
  desktop: PLACES_MAP_PIN_DEFS_PORTRAIT,
  mobile: PLACES_MAP_PIN_DEFS_PORTRAIT,
};

/**
 * Destination pins for the active map orientation.
 * (`mode` retained for call-site compatibility; unused for pin selection.)
 */
export function placesMapPinsFor(
  _mode: BackyardPresentationMode,
  orientation: PlacesMapOrientation = 'portrait',
): readonly PlacesMapPinHit[] {
  return PLACES_MAP_PINS_BY_ORIENTATION[orientation];
}

type LayoutAuthoring = {
  x?: number;
  y?: number;
  widthFrac?: number;
  heightFrac?: number;
  rotationDeg?: number;
  depth?: number;
  flipX?: boolean;
  quadCorners?: QuadCorners;
  layouts?: LayoutProfilePair;
  physicalHeightFeet?: number;
  physicalWidthFeet?: number;
  readabilityMultiplier?: number;
  artisticCompression?: number;
  id?: string;
  sprite?: { imageWidth: number; imageHeight: number };
  spriteFrame?: { width: number; height: number };
};

/**
 * Resolve the active layout transform. Prefers `layouts[mode]`, then
 * tablet → mobile, then `layouts.desktop`, then legacy flat fields.
 */
export function resolveLayoutTransform(
  def: LayoutAuthoring,
  mode: BackyardPresentationMode,
): PropLayoutTransform {
  const fromLayouts =
    def.layouts?.[mode] ??
    (mode === 'tablet' ? def.layouts?.mobile : undefined) ??
    def.layouts?.desktop;
  if (fromLayouts) {
    return {
      ...fromLayouts,
      quadCorners: fromLayouts.quadCorners ?? def.quadCorners,
    };
  }
  return {
    x: def.x ?? 0.5,
    y: def.y ?? 0.5,
    widthFrac: def.widthFrac ?? 0.1,
    ...(def.heightFrac != null ? { heightFrac: def.heightFrac } : {}),
    rotationDeg: def.rotationDeg,
    depth: def.depth,
    ...(def.flipX ? { flipX: true } : {}),
    quadCorners: def.quadCorners,
  };
}

export interface WorldScaleResolveContext {
  sceneId: string;
  worldAspect: number;
}

/** Apply layout transform onto a def copy for placeObject / placeDecal. */
export function withResolvedLayout<T extends LayoutAuthoring>(
  def: T,
  mode: BackyardPresentationMode,
  scaleCtx?: WorldScaleResolveContext,
): T & PropLayoutTransform {
  const layout = resolveLayoutTransform(def, mode);
  let widthFrac = layout.widthFrac;
  const editorScale = layout.scale ?? 1;

  if (scaleCtx) {
    const computed = computeLayoutWidthFrac(def, layout.y, editorScale, scaleCtx);
    if (computed != null) widthFrac = computed;
  }

  return {
    ...def,
    x: layout.x,
    y: layout.y,
    widthFrac,
    ...(layout.scale != null ? { scale: layout.scale } : {}),
    ...(layout.heightFrac != null ? { heightFrac: layout.heightFrac } : {}),
    rotationDeg: layout.rotationDeg,
    depth: layout.depth,
    flipX: layout.flipX === true,
    ...(layout.quadCorners ? { quadCorners: layout.quadCorners } : {}),
  };
}

function computeLayoutWidthFrac(
  def: LayoutAuthoring,
  y: number,
  editorScale: number,
  scaleCtx: WorldScaleResolveContext,
): number | null {
  const fromDef =
    def.physicalHeightFeet != null || def.physicalWidthFeet != null
      ? {
          physicalHeightFeet: def.physicalHeightFeet,
          physicalWidthFeet: def.physicalWidthFeet,
          readabilityMultiplier: def.readabilityMultiplier,
          artisticCompression: def.artisticCompression,
        }
      : def.id
        ? physicalSizeForObjectId(def.id)
        : null;
  if (!fromDef) return null;

  const frame = def.spriteFrame;
  const sprite = def.sprite;
  const imageAspect = frame
    ? frame.height / Math.max(1, frame.width)
    : sprite
      ? sprite.imageHeight / Math.max(1, sprite.imageWidth)
      : 1;

  return resolvePhysicalWidthFrac({
    physicalHeightFeet: fromDef.physicalHeightFeet,
    physicalWidthFeet: fromDef.physicalWidthFeet,
    imageAspect,
    y,
    worldAspect: scaleCtx.worldAspect,
    sceneId: scaleCtx.sceneId,
    editorScale,
    readabilityMultiplier: fromDef.readabilityMultiplier,
    artisticCompression: fromDef.artisticCompression,
  });
}

function cloneTransform(t: PropLayoutTransform): PropLayoutTransform {
  return {
    ...t,
    ...(t.quadCorners
      ? {
          quadCorners: [
            { ...t.quadCorners[0] },
            { ...t.quadCorners[1] },
            { ...t.quadCorners[2] },
            { ...t.quadCorners[3] },
          ] as PropLayoutTransform['quadCorners'],
        }
      : {}),
  };
}

/** Build / update layouts when saving one profile; never mutates the others. */
export function mergeLayoutProfile(
  existing: LayoutProfilePair | undefined,
  mode: BackyardPresentationMode,
  next: PropLayoutTransform,
  fallbackFlat?: PropLayoutTransform,
): LayoutProfilePair {
  const desktop = cloneTransform(
    existing?.desktop ?? fallbackFlat ?? next,
  );
  const mobile = cloneTransform(
    existing?.mobile ?? fallbackFlat ?? next,
  );
  // Tablet seeds from mobile when missing — always a deep clone, never a shared ref.
  const tablet = cloneTransform(
    existing?.tablet ?? existing?.mobile ?? fallbackFlat ?? next,
  );
  switch (mode) {
    case 'desktop':
      return { desktop: cloneTransform(next), tablet, mobile };
    case 'tablet':
      return { desktop, tablet: cloneTransform(next), mobile };
    case 'mobile':
      return { desktop, tablet, mobile: cloneTransform(next) };
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}
